import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ExportTargetDto } from 'core/models/export';
import { OutputType, PartType } from 'core/models/filename';

import {
  BusinessUnitDto,
  JobDetailsDto,
  JobFormDto,
  JobStatus,
  QueryHierarchy,
} from 'core/models/job';
import { jobService } from 'core/services/JobService';
import type { RootState } from 'core/store';
import { FetchStatus } from 'core/utils';

export interface JobsState {
  queries: QueryHierarchy;
  queriesStatus: FetchStatus;
  stores: BusinessUnitDto[];
  storesStatus: FetchStatus;
  jobDetail: JobDetailsDto;
  jobDetailsError: string | undefined;
}

const defaultExportTarget: ExportTargetDto = {
  url: '',
  outputType: 'excel' as OutputType,
  name: [{ partType: PartType.QueryName }],
};

const placeholderJob: JobDetailsDto = {
  id: -1,
  activeFrom: '',
  activeTo: '',
  description: '',
  isActive: true,
  query: { name: '', id: -1 },
  queryParameters: [],
  repetition: { type: 'daily' },
  stores: [],
  title: '',
  authorName: '',
  exportStatus: { status: JobStatus.Scheduled, date: '' },
  exportTargetSettings: defaultExportTarget,
};

const initialState: JobsState = {
  jobDetailsError: undefined,
  queries: { category: '', childCategories: [], queries: [] },
  queriesStatus: 'idle',
  jobDetail: placeholderJob,
  stores: [],
  storesStatus: 'idle',
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    errorConsumed: (state, _action) => {
      state.jobDetailsError = undefined;
    },
    jobDetailsLoaded: (state, action) => {
      state.jobDetail = action.payload;
    },
    jobActiveStatusChanged: (state, action) => {
      state.jobDetail.isActive = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.jobDetail = action.payload;
        state.jobDetailsError = undefined;
      })
      .addCase(fetchJob.rejected, (state, action) => {
        if (action.payload === 404) {
          state.jobDetailsError = 'NOT_FOUND';
        } else {
          state.jobDetailsError = 'UNKNOWN';
          state.jobDetail = placeholderJob;
        }
      });
    builder
      .addCase(fetchQueries.pending, (state, _action) => {
        state.queriesStatus = 'loading';
      })
      .addCase(fetchQueries.fulfilled, (state, action) => {
        state.queriesStatus = 'success';
        state.queries = action.payload;
      })
      .addCase(fetchQueries.rejected, (state, action) => {
        state.queriesStatus = 'error';
        state.jobDetailsError = action.error.message;
      });
    builder
      .addCase(fetchStores.pending, (state, _action) => {
        state.storesStatus = 'loading';
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.storesStatus = 'success';
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.storesStatus = 'error';
        state.jobDetailsError = action.error.message;
      });
  },
});

export const fetchJob = createAsyncThunk<JobDetailsDto, number>(
  'jobs/fetchJob',
  async (jobId: number, thunkApi) => {
    const rootState = thunkApi.getState() as RootState;
    if (rootState.jobs.jobDetail) {
      if (rootState.jobs.jobDetail.id === jobId) {
        return thunkApi.fulfillWithValue(rootState.jobs.jobDetail);
      }
    }
    const response = await jobService.getJob(jobId);
    if (response.status !== 200) {
      return thunkApi.rejectWithValue(response.status);
    }
    return response.json();
  }
);

export const fetchQueries = createAsyncThunk<QueryHierarchy>(
  'jobs/fetchQueries',
  async () => {
    const response = await jobService.getQueries();
    return response.json();
  }
);

export const fetchStores = createAsyncThunk<BusinessUnitDto[]>(
  'jobs/fetchStores',
  async () => {
    const response = await jobService.getStores();
    return response.json();
  }
);

export const postJob = createAsyncThunk<JobFormDto, JobFormDto>(
  'jobs/postNew',
  async (job: JobFormDto) => {
    const response = await jobService.postNewJob(job);
    if (response.status !== 200 && response.status !== 201) {
      return Promise.reject(response.json());
    }
    return response.json();
  }
);

export const { jobDetailsLoaded } = jobsSlice.actions;

export default jobsSlice;
export const selectAllJobs = (state: RootState): JobsState => state.jobs;

export const selectStores = (state: RootState) => state.jobs.stores;
export const selectQueries = (state: RootState) => state.jobs.queries;
export const selectQueriesStatus = (state: RootState) =>
  state.jobs.queriesStatus;
export const selectStoresStatus = (state: RootState) => state.jobs.storesStatus;
export const selectJobDetail = (state: RootState) => state.jobs.jobDetail;
export const selectJobDetailsError = (state: RootState) =>
  state.jobs.jobDetailsError;
